import React, {useRef} from 'react';
import {
    Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    IconButton, MenuItem, Modal, TextField, Typography, useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import {observer} from 'mobx-react';
import FilledButton from "../../../components/buttons/FilledButton";

const TEAMS_HEADER_COLOR = '#464775';
const MAIL_PREVIEW_MAXIMUM_HEIGHT = 600;

function KamDailyReportPreviewModal({feature}) {
    const theme = useTheme();
    const selectedPreview = feature.selectedPreview;

    if (!selectedPreview) {
        return null;
    }

    return (
        <Modal open={feature.isPreviewOpen} onClose={() => feature.closePreview()}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 700,
                maxHeight: '90vh',
                overflowY: 'auto',
                p: 4,
                borderRadius: '8px',
                backgroundColor: 'background.paper',
                "@media (max-width: 768px)": {
                    width: "95%",
                },
            }}>
                <IconButton onClick={() => feature.closePreview()} sx={{position: 'absolute', top: 5, right: 5}}>
                    <CloseIcon/>
                </IconButton>

                <Typography variant="h6" sx={{fontFamily: theme.brand.fonts.display, fontSize: '19px', mb: 0.5}}>
                    Vista previa — Clientes bloqueados por mora
                </Typography>
                <Typography variant="body2" sx={{color: theme.palette.text.secondary, fontSize: '12.5px', mb: 1.5}}>
                    Ejemplo con los datos de hoy — así se vería el envío que recibiría {selectedPreview.recipientLabel} por
                    mail y por Teams. Se genera y envía una versión personalizada para cada uno de
                    los {feature.recipientsCount} KAMs con clientes en mora.
                </Typography>

                <RecipientSelector feature={feature}/>

                <MailPreview preview={selectedPreview} theme={theme}/>
                <TeamsPreview preview={selectedPreview} theme={theme}/>

                <Box sx={{mt: 3, display: 'flex', justifyContent: 'flex-end'}}>
                    <FilledButton onClick={() => feature.openConfirmation()}>
                        Enviar por mail y Teams
                    </FilledButton>
                </Box>

                <SendConfirmationDialog feature={feature}/>
            </Box>
        </Modal>
    );
}

function RecipientSelector({feature}) {
    return (
        <TextField
            select
            label="Destinatario"
            fullWidth
            value={feature.selectedIndex}
            onChange={(event) => feature.selectRecipientAt(Number(event.target.value))}
            sx={{mb: 2}}
        >
            {feature.recipientOptions().map(option => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
}

function MailPreview({preview, theme}) {
    const mailIframeRef = useRef(null);

    const resizeMailIframeToItsContent = () => {
        const mailIframeDocument = mailIframeRef.current?.contentDocument;
        if (!mailIframeDocument) {
            return;
        }
        const contentHeight = mailIframeDocument.documentElement.scrollHeight;
        mailIframeRef.current.style.height = `${Math.min(contentHeight, MAIL_PREVIEW_MAXIMUM_HEIGHT)}px`;
    };

    return (
        <Box sx={{border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', overflow: 'hidden', mt: 1}}>
            <Box sx={{
                backgroundColor: theme.brand.field,
                padding: '10px 14px',
                fontSize: '12px',
                color: theme.palette.text.secondary,
                borderBottom: `1px solid ${theme.palette.divider}`,
            }}>
                <Box sx={{py: 0.25}}>
                    <Box component="b" sx={{color: theme.palette.text.primary}}>De:</Box> Cuentas Corrientes — Sapore Di
                    Pane &lt;{preview.email.sender}&gt;
                </Box>
                <Box sx={{py: 0.25}}>
                    <Box component="b" sx={{color: theme.palette.text.primary}}>Para:</Box> {preview.recipientLabel}
                </Box>
                <Box sx={{py: 0.25}}>
                    <Box component="b" sx={{color: theme.palette.text.primary}}>Asunto:</Box> {preview.email.subject}
                </Box>
            </Box>
            <iframe
                ref={mailIframeRef}
                title="Vista previa del mail"
                srcDoc={preview.email.htmlContent}
                sandbox="allow-same-origin"
                onLoad={resizeMailIframeToItsContent}
                style={{width: '100%', height: 350, border: 'none', display: 'block'}}
            />
        </Box>
    );
}

function TeamsPreview({preview, theme}) {
    const teams = preview.teams;
    return (
        <Box sx={{border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', overflow: 'hidden', mt: 3}}>
            <Box sx={{
                backgroundColor: TEAMS_HEADER_COLOR,
                color: '#fff',
                padding: '9px 14px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
            }}>
                <ChatBubbleOutlineIcon sx={{fontSize: 15}}/>
                <Box component="b">Microsoft Teams</Box> — Chat con {preview.recipientLabel}
            </Box>
            <Box sx={{
                backgroundColor: theme.brand.field,
                padding: '14px',
                fontSize: '13px',
                lineHeight: 1.65,
            }}>
                <Typography sx={{fontWeight: 700, mb: 1}}>{teams.title}</Typography>
                {teams.intro &&
                    <Typography variant="body2" sx={{mb: 1, color: theme.palette.text.secondary}}>{teams.intro}</Typography>}
                {teams.facts.map(fact => (
                    <Typography key={fact.label} variant="body2">
                        <Box component="b" sx={{color: theme.palette.text.primary}}>{fact.label}:</Box> {fact.value}
                    </Typography>
                ))}
                {teams.notes.map(note => (
                    <Typography key={note} variant="body2" sx={{mt: 1, color: theme.palette.text.secondary}}>
                        {note}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}

function SendConfirmationDialog({feature}) {
    return (
        <Dialog open={feature.isConfirmationOpen} onClose={() => feature.cancelSending()}>
            <DialogTitle>Confirmar envío</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    ¿Enviar el reporte a {feature.recipientsCount} destinatarios ({feature.customersCount} clientes)?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <FilledButton onClick={() => feature.cancelSending()}>
                    Cancelar
                </FilledButton>
                <FilledButton onClick={() => feature.sendReports()}>
                    Enviar
                </FilledButton>
            </DialogActions>
        </Dialog>
    );
}

export default observer(KamDailyReportPreviewModal);
